import styled from '@emotion/styled';
import {IconUnfoldMore} from '@travel-frontend/icons';
import {
  ActionLink,
  Dialog,
  DialogBody,
  ParagraphSmall,
  Select,
} from '@travel-frontend/ui-components';
import {useState} from 'react';
import {useTranslation} from 'react-i18next';

import {CloseButton} from '@/components/CloseButton';
import {HelpTooltip} from '@/components/HelpTooltip';
import {Container} from '@/modules/common/components/SortingDropdown/components/Container';
import {DialogContent} from '@/modules/common/components/SortingDropdown/components/DialogContent';
import {useSortingOptions} from '@/modules/common/components/SortingDropdown/useSortingOptions';
import {useSortParams} from '@/modules/common/hooks/useSortParams';
import {testId} from '@/utils/testId';

type Props = {
  isCentered?: boolean;
};

export const SortingDropdown = styled(
  ({isCentered = false, ...restProps}: Props) => {
    const {t} = useTranslation('general');

    const [sortParams, setSortParams] = useSortParams();

    const [descriptionDialogOpen, setDescriptionDialogOpen] = useState(false);
    const openDescriptionDialog = () => setDescriptionDialogOpen(true);
    const closeDescriptionDialog = () => setDescriptionDialogOpen(false);

    const options = useSortingOptions();
    const activeOption = options.find(
      (option) => option.value === sortParams.sortBy
    );
    const activeOptionHasHint = Boolean(activeOption?.hint);
    const activeOptionHasDescription = Boolean(activeOption?.description);

    const styles: any = {
      menuPortal: (base: any) => ({...base, zIndex: 9999}),
    };

    return (
      <Container {...restProps}>
        <Select
          aria-label={t('sortingDropdown.label')}
          menuPortalTarget={document.body}
          menuPosition="fixed"
          menuShouldBlockScroll
          value={activeOption}
          options={options}
          onChange={(option: any) => {
            setSortParams((previous) => ({
              ...previous,
              sortBy: option.value,
            }));
          }}
          styles={styles}
          isSearchable={false}
          dropdownIndicatorIcon={<IconUnfoldMore />}
          // @ts-expect-error ...
          formatSingleValue={(state) => ({
            prefix: isCentered ? '' : t('sortingDropdown.prefix'),
            value: state.data.label,
          })}
          highlightBehaviour="always"
          centered={isCentered}
          data-testid={testId('sorting')}
        />
        {(activeOptionHasHint || activeOptionHasDescription) && (
          <>
            <HelpTooltip
              placement="left"
              content={
                <ParagraphSmall>
                  {activeOption?.hint || activeOption?.description}
                  {activeOptionHasHint && activeOptionHasDescription && (
                    <ActionLink onClick={openDescriptionDialog}>
                      {t('learnMore')}
                    </ActionLink>
                  )}
                </ParagraphSmall>
              }
            />
            {activeOptionHasHint && activeOptionHasDescription && (
              <Dialog
                open={descriptionDialogOpen}
                onClose={closeDescriptionDialog}
              >
                <DialogBody>
                  <DialogContent>
                    <CloseButton onClick={closeDescriptionDialog} />
                    {activeOption?.description}
                  </DialogContent>
                </DialogBody>
              </Dialog>
            )}
          </>
        )}
      </Container>
    );
  }
)``;
